function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import ServiceFactory from "../../service";
import account from "../../data/account";

// Keyword Intelligence shared bits. API root: /v1/market-research/keywords/*
// (the existing `research` endpoint type, same host/auth as Market Trends /
// Company Analysis; x-app-id + bearer ride in via the Service framework).
export function kwService(api) {
  const Service = ServiceFactory.getType(`/keywords${api}`, "research");
  return new Service();
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Day key (YYYY-MM-DD) for sorting and grouping.
export function isoDay(d) {
  return d ? String(d).slice(0, 10) : "";
}

// Readable date, e.g. "Oct 6, 2026". Falls back to a dash.
export function fmtDate(d) {
  const day = isoDay(d);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day || "-";
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

// Status chip: tone = green | amber | red | grey.
export function Chip({
  tone = "grey",
  title,
  children
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: `aps-chip ${tone}`,
    title: title || undefined
  }, children);
}

// Position chip for a rank (lower is better; null = not in the top 100).
export function PositionChip({
  position,
  previous,
  url
}) {
  if (position == null) return /*#__PURE__*/React.createElement(Chip, {
    tone: "grey",
    title: "Not in the top 100 results"
  }, "Not in top 100");
  const cur = Number(position);
  const prev = previous == null ? null : Number(previous);
  let move = null;
  if (prev != null && prev !== cur) {
    const up = cur < prev;
    move = /*#__PURE__*/React.createElement("span", {
      className: `aps-move ${up ? "up" : "down"}`
    }, up ? "up" : "down", " ", Math.abs(prev - cur));
  }
  return /*#__PURE__*/React.createElement("span", {
    className: "aps-pos"
  }, /*#__PURE__*/React.createElement(Chip, {
    tone: cur <= 10 ? "green" : "amber",
    title: url || undefined
  }, "#", cur), move);
}

// Back-compat for older callers: one overview row.
export function PositionCell({
  row
}) {
  return /*#__PURE__*/React.createElement(PositionChip, {
    position: row ? row.currentPosition : null,
    previous: row ? row.previousPosition : null,
    url: row ? row.rankedUrl : null
  });
}

// Quiet "···" row menu. items = [{ label, onClick, danger, confirm }].
// An item with `confirm` asks once more (the label swaps to the confirm text).
export class RowMenu extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onDoc", e => {
      if (this.ref.current && !this.ref.current.contains(e.target)) this.close();
    });
    _defineProperty(this, "open", e => {
      e.stopPropagation();
      if (this.state.open) {
        this.close();
        return;
      }
      this.setState({
        open: true,
        arming: null
      });
      document.addEventListener("mousedown", this.onDoc);
    });
    _defineProperty(this, "close", () => {
      document.removeEventListener("mousedown", this.onDoc);
      this.setState({
        open: false,
        arming: null
      });
    });
    _defineProperty(this, "pick", (item, i) => e => {
      e.stopPropagation();
      if (item.confirm && this.state.arming !== i) {
        this.setState({
          arming: i
        });
        return;
      }
      this.close();
      item.onClick && item.onClick();
    });
    this.state = {
      open: false,
      arming: null
    };
    this.ref = /*#__PURE__*/React.createRef();
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDoc);
  }
  render() {
    const {
      items,
      disabled
    } = this.props;
    const {
      open,
      arming
    } = this.state;
    return /*#__PURE__*/React.createElement("span", {
      className: "aps-menu",
      ref: this.ref,
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "aps-menu-btn",
      "aria-label": "More actions",
      disabled: disabled,
      onClick: this.open
    }, "\xB7\xB7\xB7"), open && /*#__PURE__*/React.createElement("span", {
      className: "aps-menu-pop",
      role: "menu"
    }, items.filter(Boolean).map((it, i) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      role: "menuitem",
      key: it.label,
      className: it.danger ? "danger" : "",
      onClick: this.pick(it, i)
    }, arming === i ? it.confirm : it.label))));
  }
}

// Verdict card: a colored dot, one bold line, one plain sentence.
export function Verdict({
  tone = "grey",
  title,
  children
}) {
  return /*#__PURE__*/React.createElement("section", {
    className: `aps-verdict ${tone}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "aps-dot"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, title), children ? /*#__PURE__*/React.createElement("span", null, children) : null));
}
export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many || `${one}s`}`;
}

// ------------------------------------------------------------ your own site
// Keyword ranks and AI mentions are measured against the competitor
// registry's "self" row (is_self). Without one, every rank reads "not in the
// top 100" and every AI answer reads "not mentioned", even when the answer
// names the business or its site ranks first. So the pages ask for it first.

export function normDomain(d) {
  return String(d || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0];
}
export const selfRow = competitors => (competitors || []).find(c => c.isSelf && c.active !== false) || null;

/**
 * Save `domain` as the workspace's own site. The service keeps an existing
 * row for a domain as it is (a duplicate add returns the old row), so a site
 * already saved as a competitor is removed and added again as yours.
 */
export async function saveSelfSite(domain, competitors) {
  const d = normDomain(domain);
  if (!d) throw new window.Error("domain required");
  const same = (competitors || []).find(c => normDomain(c.domain) === d);
  if (same && same.isSelf) return same;
  if (same) await kwService("/competitors").deleteAsync(same.id);
  return kwService("/competitors").postAsync({
    domain: d,
    label: null,
    isSelf: true
  });
}

/** "Which website is yours?" card. Props: competitors, onSaved(), what (sentence on what it unlocks). */
export class SelfSiteCard extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "save", async () => {
      if (this.state.saving || !normDomain(this.state.domain)) return;
      this.setState({
        saving: true,
        failed: false
      });
      try {
        await saveSelfSite(this.state.domain, this.props.competitors);
        this.setState({
          saving: false
        });
        if (this.props.onSaved) this.props.onSaved();
      } catch (e) {
        this.setState({
          saving: false,
          failed: true
        });
      }
    });
    this.state = {
      domain: normDomain((account.get() || {}).domain),
      saving: false,
      failed: false
    };
  }
  render() {
    const {
      domain,
      saving,
      failed
    } = this.state;
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-verdict grey aps-selfsite"
    }, /*#__PURE__*/React.createElement("span", {
      className: "aps-dot"
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Which website is yours?"), /*#__PURE__*/React.createElement("span", null, this.props.what || "We need it to tell your results apart from your competitors'."), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement("input", {
      className: "wide",
      placeholder: "yourstore.com",
      "aria-label": "Your website",
      value: domain,
      onChange: e => this.setState({
        domain: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.save();
      }
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: saving || !normDomain(domain),
      onClick: this.save
    }, saving ? "Saving…" : "This is my site")), failed ? /*#__PURE__*/React.createElement("span", null, "Saving didn't work. Try again.") : null));
  }
}